import React, { FC, useEffect, useRef, useState } from "react";
import { ListSubheader, TextField, Tooltip } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useAccountBySearch } from "components/summary/hooks";
import { Autocomplete } from "@mui/material";
import { AutocompleteRenderGroupParams } from "@mui/material";
import { ChevronRight, Done, Info } from "@mui/icons-material";
import { DetailAccount, MatchedItems } from "types/summary";
import { debounce } from "lodash";
import SearchDetailComponent from "components/app/SearchDetailComponent";
import styles from "./index.module.scss";
import { Portal } from "@mui/material";
import { InfoType } from "components/ai-chat/types";

const useStyle = makeStyles(() => ({
  option: {
    color: "#333",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    overflow: "hidden",
    textOverflow: "ellipsis",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: "#eee",
    },
  },
  inputRoot: {
    flexWrap: "nowrap",
    paddingRight: "4px !important",
    backgroundColor: "#fff",
  },
  loading: {
    width: 544,
  },
  tooltip: { maxHeight: "50vh" },
  labelTxt: {
    textTransform: "uppercase",
    fontStyle: "italic",
    fontWeight: 600,
    color: "#fff",
    fontSize: "10px !important",
    whiteSpace: "nowrap",
  },
  labelWrapper: {
    background: "#B2B2B2",
    borderRadius: 5,
    padding: "2px 4px",
    marginLeft: 10,
  },
}));

export interface AccountOption {
  accountName: string;
  accountId: string;
  accountKey: string;
  matched?: MatchedItems;
  groupName: string;
}

let lastQuery: any;

const Account: FC<{
  row: InfoType;
  setRows: React.Dispatch<React.SetStateAction<InfoType[]>>;
  index: number;
  disabled?: boolean;
}> = ({ row, setRows, index, disabled }) => {
  const [keyword, setKeyword] = useState("");
  const [query, setQuery] = useState("");

  const [options, setOptions] = useState<AccountOption[]>([]);
  const [loading, setLoading] = useState(false);
  const { data: accountList, error, isValidating } = useAccountBySearch(query);
  const [curAccountId, setCurAccountId] = useState("");
  const [curConfidential, setCurConfidential] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [open, setOpen] = useState(false);
  const [popperPosition, setPopperPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLDivElement | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);
  const hideTimer = useRef<NodeJS.Timeout | null>(null);

  const classes = useStyle({ options });
  const generateOption = (accounts: DetailAccount[]) =>
    accounts.map((x) => ({
      accountName: x.accountName,
      accountId: x.accountId,
      accountKey: x.accountKey,
      matched: x.items[0],
      groupName: x.assignedOrTagged ? "Most Related Account(s)" : "Other",
    }));

  const handleInputChange = (e: any, value: string) => {
    setKeyword(value);
    if (inputRef?.current === e?.target && inputRef?.current?.contains(e?.target as Node))
      setOpen(true);
    if (e?.type === "blur") {
      setKeyword(e.target.value);
    }
  };

  useEffect(() => {
    if (keyword && keyword.length > 2) {
      setLoading(true);
      setOptions([]);
      const debouncedSetQuery = debounce(() => {
        setQuery(`keyword=${keyword}`);
      }, 500);
      lastQuery?.cancel?.();
      lastQuery = debouncedSetQuery;
      debouncedSetQuery();
    }
  }, [keyword]);

  useEffect(() => {
    if (accountList && !isValidating) {
      setOptions(generateOption(accountList));
      setLoading(false);
    }
  }, [accountList, isValidating]);

  useEffect(() => {
    if (error) {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => {
    if (open && popperRef.current) {
      const rect = popperRef.current.getBoundingClientRect();
      setPopperPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    }
  }, [open, options]);

  useEffect(() => {
    return () => {
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    const closePop = (e: Event) => {
      if (
        !listRef?.current?.contains(e.target as Node) &&
        listRef?.current !== e.target &&
        inputRef?.current !== e.target &&
        !inputRef?.current?.contains(e.target as Node) &&
        !disabled
      ) {
        setOpen(false);
        setShowDetail(false);
      } else if (
        inputRef?.current === e.target &&
        inputRef?.current?.contains(e.target as Node) &&
        !disabled
      ) {
        setOpen(true);
      }
    };
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, [disabled]);

  const renderGroup = (params: AutocompleteRenderGroupParams) => [
    <ListSubheader
      key={params.key}
      disableSticky={true}
    >
      {params.group}
    </ListSubheader>,
    <div
      key={`${params.key}_children`}
      style={{ marginLeft: 16 }}
    >
      {params.children}
    </div>,
  ];

  const handleHoverOption = (option: any) => () => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    setShowDetail(true);
    setCurAccountId(option.accountId);
    setCurConfidential(option.confidential);
  };

  return (
    <div className={styles["account-container"]}>
      <Autocomplete
        classes={{
          listbox: "listbox",
          option: classes.option,
          inputRoot: classes.inputRoot,
        }}
        open={open}
        value={
          row && row.name
            ? ({
                accountName: row.name,
                accountId: typeof row.id === "string" ? row.id : "",
                accountKey: "",
                groupName: "",
              } as AccountOption)
            : null
        }
        disabled={disabled}
        isOptionEqualToValue={(o, v) => o.accountId === v.accountId}
        getOptionLabel={(option) => option.accountName}
        inputValue={keyword}
        renderGroup={renderGroup}
        noOptionsText={
          keyword && keyword.length > 2
            ? "No accounts found"
            : "Please search at least 3 characters"
        }
        filterOptions={(x) => x}
        groupBy={(o) => o.groupName}
        onInputChange={handleInputChange}
        loading={loading}
        options={options}
        slotProps={{
          popper: {
            ref: popperRef,
            sx: {
              zIndex: (theme) => theme.zIndex.modal + 3,
            },
          },
        }}
        renderOption={(props, option, { selected }) => (
          <li
            {...props}
            key={option.accountId}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
              onMouseEnter={handleHoverOption(option)}
              onMouseLeave={() => {
                if (hideTimer.current) {
                  clearTimeout(hideTimer.current);
                }
                hideTimer.current = setTimeout(() => {
                  setShowDetail(false);
                }, 200);
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ flexBasis: "100%" }}>
                  <p>{option.accountName}</p>
                  <p style={{ color: "#777777", whiteSpace: "nowrap" }}>
                    {option.matched?.matchedValue || option.accountId}
                    <span className={classes.labelWrapper}>
                      <span className={classes.labelTxt}>
                        {option.matched?.matchedField || "Account Id"}
                      </span>
                    </span>
                  </p>
                </div>
                {selected ? <Done style={{ color: "#007AA3" }} /> : null}
              </div>
              <ChevronRight fontSize="small" />
            </div>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            margin="normal"
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              type: "search",
              inputRef,
              startAdornment: (
                <>
                  {params.InputProps.startAdornment}
                  <Tooltip
                    classes={{ tooltip: classes.tooltip }}
                    arrow={true}
                    title={
                      <div>
                        <b>Enterprise</b>
                        <ul style={{ whiteSpace: "nowrap" }}>
                          <li>Account Name</li>
                          <li>Account ID</li>
                          <li>Contract ID</li>
                          <li>Offer Code</li>
                          <li>Organization Name</li>
                          <li>Site Name</li>
                          <li>SKU Description</li>
                          <li>SKU ID</li>
                          <li>Subscription ID</li>
                          <li>Web Order ID</li>
                        </ul>
                        <b>Online</b>
                        <ul>
                          <li>Email</li>
                        </ul>
                      </div>
                    }
                  >
                    <Info
                      color="primary"
                      style={{
                        transform: "translate(0px, -2px)",
                        color: "#00a0d1",
                        zIndex: 1000,
                        fontSize: "0.75rem",
                        verticalAlign: "center",
                        alignSelf: "center",
                        marginTop: 4,
                      }}
                    />
                  </Tooltip>
                </>
              ),
            }}
            // error={!!fieldState.error}
            // helperText={fieldState.error?.message}
            size={"small"}
            placeholder={!keyword ? "Please search at least 3 characters" : ""}
          />
        )}
        onChange={(_e: any, value) => {
          setCurAccountId(value ? value.accountId : "");
          setRows((prev) => {
            const newRows = [...prev];
            const prevRow = newRows[index] || {};
            newRows[index] = {
              ...prevRow,
              type: "account",
              id: value ? value.accountId : "",
              name: value ? value.accountName : "",
            };
            return newRows;
          });
        }}
      />
      {showDetail && open && popperPosition.width > 0 && (
        <Portal>
          <div
            ref={listRef}
            className={styles["detail-panel"]}
            style={{
              left: popperPosition.left + popperPosition.width + 4,
              top: popperPosition.top,
              maxHeight: popperPosition.height > 400 ? popperPosition.height : 400,
            }}
            onMouseEnter={() => {
              if (hideTimer.current) {
                clearTimeout(hideTimer.current);
                hideTimer.current = null;
              }
              setShowDetail(true);
            }}
            onMouseLeave={() => {
              if (hideTimer.current) {
                clearTimeout(hideTimer.current);
              }
              hideTimer.current = setTimeout(() => {
                setShowDetail(false);
              }, 200);
            }}
          >
            <SearchDetailComponent
              showDetail={showDetail}
              curAccountId={curAccountId}
              curConfidential={curConfidential}
            />
          </div>
        </Portal>
      )}
    </div>
  );
};

export default Account;
